import { SetStateAction, Suspense, useState } from "react";
import { Output } from "valibot";

import LoadingPlaceholder from "components/LoadingPlaceholder";
import MessageWithLinks from "components/MessageWithLinks";
import { chatEndedDueToInactivityMessage } from "components/SonicBot/constants";
import {
  AttachmentSchema,
  BotInfoSchema,
  ChatDataSchema,
  ChatMessageTypeSchema,
  SupportTicketInfoSchema,
  UserFormInfoSchema,
} from "components/SonicBot/schemas";
import TicketInfoChatMessage from "components/TicketInfo/components/TicketInfoChatMessage";
import { saveLikeDislike } from "shared/api";
import { useConfigContext } from "shared/hooks";
import { AppConfiguration, Literals, Source } from "shared/types";
import classNames from "shared/utils/classNames";

import AgentInfoMessage from "./AgentInfoMessage";
import Attachment from "./Attachment";
import JumpingDots from "./JumpingDots";
import LikeButton from "./LikeButton";
import MessageAvatar from "./MessageAvatar";
import Sources from "./Sources";

import "./index.css";

type Props = {
  id: string;
  botInfo: Output<typeof BotInfoSchema>;
  isSent: boolean;
  message: string;
  sources?: Source[];
  imageUrls?: string[];
  renderableSource?: string;
  config: AppConfiguration;
  chatId: string;
  chatData: Output<typeof ChatDataSchema>;
  setChatData: React.Dispatch<SetStateAction<any[]>>;
  isLastMessage?: boolean;
  copyTexts: Literals;
  showLikeDislikeBtn?: boolean;
  chatMessageType?: Output<typeof ChatMessageTypeSchema>;
  avatarUrl?: string;
  likeStatus?: string;
  token?: string;
  isConversationalEndResponse?: boolean;
  submittedFormFieldData?: Output<typeof UserFormInfoSchema>;
  ticketInfo?: Output<typeof SupportTicketInfoSchema>;
  onTicketInfoClick?: (
    ticketInfo: Output<typeof SupportTicketInfoSchema>,
  ) => void;
};

function determineMessagePadding(
  isIframe: boolean,
  isSent: boolean,
  isConversational: boolean | undefined,
  isConversationalEndResponse: boolean,
): string {
  if (!isIframe) {
    return "";
  }

  if (isSent || !isConversational) {
    return "p-4";
  }

  return isConversationalEndResponse ? "px-4 pb-4" : "px-4";
}
function MessageBubble(props: Props) {
  const { isIframe } = useConfigContext();

  const {
    id,
    botInfo,
    isSent,
    message,
    sources,
    config,
    chatId,
    chatData,
    setChatData,
    isLastMessage,
    copyTexts,
    showLikeDislikeBtn = true,
    imageUrls,
    renderableSource,
    chatMessageType,
    avatarUrl,
    likeStatus: originalLikeStatus = "",
    isConversationalEndResponse = true,
    submittedFormFieldData,
    ticketInfo,
    onTicketInfoClick,
  } = props;
  const [likeStatus, setLikeStatus] = useState<string>(originalLikeStatus);

  const handleLikeDislike = (status: string) => {
    setLikeStatus(status);
    setChatData(
      chatData.map(item => {
        if (item.id === id) {
          return { ...item, like_status: status };
        }
        return item;
      }),
    );

    saveLikeDislike({
      token: config.token,
      chatId,
      messageId: id,
      likeStatus: status,
    });
  };

  const generationInProgress = isSent === false && !message?.trim();
  const showJumpingDots =
    generationInProgress || id === "jumping-dots-container";

  if (
    ["waiting_for_live_agent", "handoff_canceled"].includes(
      chatMessageType ?? "",
    ) ||
    message === chatEndedDueToInactivityMessage
  ) {
    return null;
  }

  if (chatMessageType === "live_agent_info") {
    return <AgentInfoMessage message={message} />;
  }
  const messagePadding = determineMessagePadding(
    isIframe,
    isSent,
    botInfo.bot_prompt_config?.is_conversational,
    isConversationalEndResponse,
  );
  const shouldShowLikeDislikeBtn =
    !isSent && id !== "-1" && showLikeDislikeBtn && isConversationalEndResponse;
  const likeButtonContainerClassNames = classNames(
    "flex w-full items-end justify-end gap-2",
    isIframe ? "p-2 pt-0" : "pt-2",
  );

  const renderMessage = () => {
    switch (true) {
      case submittedFormFieldData &&
        submittedFormFieldData?.form_field?.type === "attachments" &&
        !submittedFormFieldData?.skip_submission: {
        return (
          <div className="flex flex-col gap-3">
            {(submittedFormFieldData?.form_field?.value ?? []).map(
              (attachment: Output<typeof AttachmentSchema>) => (
                <Attachment
                  url={attachment.url}
                  fileName={attachment.file_name}
                  mimetype={attachment.mimetype}
                  fileSize={attachment.file_size}
                />
              ),
            )}
          </div>
        );
      }
      case !!ticketInfo: {
        return (
          ticketInfo && (
            <div
              onClick={() => {
                onTicketInfoClick?.(ticketInfo);
              }}
              className="cursor-pointer"
            >
              <TicketInfoChatMessage
                title={ticketInfo.title}
                ticketId={ticketInfo.ticket_id}
                createdAt={ticketInfo.created_at}
                copyTexts={copyTexts}
              />
            </div>
          )
        );
      }
      default: {
        return (
          <div>
            {imageUrls?.map((url, index) => {
              return (
                <img
                  key={index}
                  src={url}
                  alt="message"
                  width={250}
                  height={250}
                />
              );
            })}

            {showJumpingDots ? (
              <JumpingDots />
            ) : (
              <Suspense
                fallback={
                  <LoadingPlaceholder
                    generationLanguage={
                      botInfo.bot_prompt_config.generation_language
                    }
                  />
                }
              >
                <MessageWithLinks
                  message={message}
                  isSent={isSent}
                  renderableSource={renderableSource}
                />
              </Suspense>
            )}

            {botInfo.bot_info_config.is_show_sources && !isSent && (
              <Sources sources={sources} copyTexts={copyTexts} />
            )}
          </div>
        );
      }
    }
  };

  return (
    <div id={id} className={classNames("likes-container", isSent && "sent")}>
      <div
        className={classNames(
          "flex max-w-full gap-2",
          isIframe && "items-end rounded-lg",
          messagePadding,
        )}
      >
        <MessageAvatar
          isSent={isSent}
          isIframe={isIframe}
          botAvatar={avatarUrl ?? botInfo.bot_info_config.chatbot_avatar}
          isLastMessage={isLastMessage}
          isHidden={!isConversationalEndResponse}
        />
        <div className={classNames("min-w-0", isSent && "ml-[60px]")}>
          {renderMessage()}
        </div>
      </div>
      {shouldShowLikeDislikeBtn && (
        <div className={likeButtonContainerClassNames}>
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="liked"
            likeStatus={likeStatus}
          />
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="disliked"
            likeStatus={likeStatus}
          />
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
